import React, { useState, useEffect } from "react";
import { Table, Thead, Tr, Th, Sortable } from "@lightspeed/cirrus-table";
import { Card } from "@lightspeed/flame/Card";
import Row from "./paymentTable/Row";
import Loader from "../Loader";
import { useSelector } from "react-redux";
import * as sortFns from "../../utils/sortFn";
import { Button } from "@lightspeed/flame/Button";
import ClassDepLocInfo from "./ClassDepLocInfo";
import OverShortInfo from "./OverShortInfo";
import { getIntegrationName, isAcombaIntegration, isAcombaSdkIntegration, isD365Integration, normalizeClassDimensionList } from "../../utils/d365Dimensions";
import { fetchAcombaSdkLists } from "../../utils/acombaSdk";
import { normalizeQuickBooksClassDepartmentList } from "../../utils/quickbooksClassDepartment";

const PaymentTable = ({ data, scrollPos, sortState, changeSortState }) => {
  const ui = useSelector((state) => state.ui);
  const database = useSelector((state) => state.database);

  const [sortedData, setSortedData] = useState([...data]);
  const [accountsList, setAccountsList] = useState([]);
  const [customersList, setCustomersList] = useState([]);
  const [classList, setClassList] = useState([]);
  const [d365DimensionsList, setD365DimensionsList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [infoModal, setInfoModal] = useState(null); // "pos" | "accounting" | null

  const sortValues = ["unsorted", "desc", "asc"];
  const reducer = (accumulator, currentValue) => accumulator + currentValue;
  const d365Integration = isD365Integration(database.currentCompany, database.currentShop);
  const quickBooksIntegration = getIntegrationName(database.currentCompany, database.currentShop) === "QUICKBOOKS";
  const acombagoIntegration = isAcombaIntegration(database.currentCompany, database.currentShop);
  const acombaSdkIntegration = isAcombaSdkIntegration(
    database.currentCompany,
    database.currentShop
  );
  const accountingColsCount = acombagoIntegration ? 6 : 7;
  const quickBooksDropShopId =
    database.currentCompany?.type === "RESTOK"
      ? database.currentShop?.area_id ?? database.currentShop?.shop_id ?? 0
      : database.currentShop?.shop_id ?? 0;

  const normalizeCustomers = (rows) =>
    Array.isArray(rows)
      ? rows
          .map((row) => {
            const id =
              row?.id ??
              row?.customer_id ??
              row?.qb_customer_id ??
              row?.customerid ??
              row?.customer_number ??
              row?.value ??
              "";
            const description =
              row?.description ??
              row?.customer_name ??
              row?.name ??
              row?.display_name ??
              row?.fully_qualified_name ??
              row?.label ??
              id;

            return {
              ...row,
              id: String(id).trim(),
              description: String(description ?? "").trim(),
            };
          })
          .filter((row) => row.id || row.description)
      : [];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        if (acombaSdkIntegration) {
          const { accounts, projects } = await fetchAcombaSdkLists({
            ip: database.currentShop?.ACOMBA_SDK_IP,
            port: database.currentShop?.ACOMBA_SDK_PORT,
          });
          setAccountsList(accounts);
          setCustomersList(normalizeCustomers(projects));
          setClassList([]);
          setD365DimensionsList([]);
          setLoading(false);
          return;
        }

        const [accRes, classRes, customerRes] = await Promise.all([
          fetch("https://reportapi.beesql.com/api/admin/retailr/report", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              company_id: database.currentCompany.id,
              shop_id: quickBooksDropShopId,
              xoption: "QuickBooksIMPORT DROP_ACCOUNT_LIST",
            }),
          }),
          fetch("https://reportapi.beesql.com/api/admin/retailr/report", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              company_id: database.currentCompany.id,
              shop_id: quickBooksDropShopId,
              xoption: quickBooksIntegration
                ? "QuickBooksIMPORT DROP_CLASSDEPARTMENT"
                : "QuickBooksIMPORT DROP_CLASS_LIST_CLASS",
            }),
          }),
          fetch("https://reportapi.beesql.com/api/admin/retailr/report", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              company_id: database.currentCompany.id,
              shop_id: quickBooksDropShopId,
              xoption: "QuickBooksIMPORT DROP_CUSTOMER_LIST",
            }),
          }),
        ]);

        const accountsData = await accRes.json();
        const classDataRaw = await classRes.json();
        const customerDataRaw = await customerRes.json();

        setAccountsList(Array.isArray(accountsData) ? accountsData : []);
        setCustomersList(normalizeCustomers(customerDataRaw));

        const cleanedClassList = quickBooksIntegration
          ? normalizeQuickBooksClassDepartmentList(classDataRaw)
          : normalizeClassDimensionList(classDataRaw);

        setClassList(cleanedClassList);
        if (d365Integration) {
          const dimensionRes = await fetch("https://reportapi.beesql.com/api/admin/retailr/report", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              company_id: database.currentCompany.id,
              shop_id: database.currentShop.shop_id,
              xoption: "D365DimensionsList",
            }),
          });
          const dimensionDataRaw = await dimensionRes.json();
          setD365DimensionsList(normalizeClassDimensionList(dimensionDataRaw));
        } else {
          setD365DimensionsList([]);
        }
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch payment mapping data:", err);
        setLoading(false);
      }
    };

    if (database?.currentCompany?.id) {
      fetchData();
    }
  }, [
    database.currentCompany.id,
    database.currentShop.shop_id,
    database.currentShop?.ACOMBA_SDK_IP,
    database.currentShop?.ACOMBA_SDK_PORT,
    quickBooksDropShopId,
    d365Integration,
    quickBooksIntegration,
    acombaSdkIntegration,
  ]);

  useEffect(() => {
    setSortedData([...data]);
  }, [data]);

  useEffect(() => {
    if (sortState.reduce(reducer) > 0) {
      let newData = [...data];

      if (sortState[0] === 1) {
        newData = [...newData.sort(sortFns.customSortGen("company_id"))];
      } else if (sortState[0] === 2) {
        newData = [...newData.sort(sortFns.customSortGen("company_id")).reverse()];
      }

      if (sortState[1] === 1) {
        newData = [...newData.sort(sortFns.customSortGen("paymenttype_id"))];
      } else if (sortState[1] === 2) {
        newData = [...newData.sort(sortFns.customSortGen("paymenttype_id")).reverse()];
      }

      if (sortState[2] === 1) {
        newData = [...newData.sort(sortFns.customSortGenString("paymenttypeid_name"))];
      } else if (sortState[2] === 2) {
        newData = [...newData.sort(sortFns.customSortGenString("paymenttypeid_name")).reverse()];
      }

      if (sortState[3] === 1) {
        newData = [...newData.sort(sortFns.customSortGenString("account_id"))];
      } else if (sortState[3] === 2) {
        newData = [...newData.sort(sortFns.customSortGenString("account_id")).reverse()];
      }

      if (sortState[4] === 1) {
        newData = [...newData.sort(sortFns.customSortGenString("account_description"))];
      } else if (sortState[4] === 2) {
        newData = [...newData.sort(sortFns.customSortGenString("account_description")).reverse()];
      }

      if (sortState[5] === 1) {
        newData = [...newData.sort(sortFns.customSortGenString("over_short_id"))];
      } else if (sortState[5] === 2) {
        newData = [...newData.sort(sortFns.customSortGenString("over_short_id")).reverse()];
      }

      if (sortState[6] === 1) {
        newData = [...newData.sort(sortFns.customSortGenString("over_short_description"))];
      } else if (sortState[6] === 2) {
        newData = [...newData.sort(sortFns.customSortGenString("over_short_description")).reverse()];
      }

      setSortedData(newData);
    } else {
      setSortedData([...data]);
    }
  }, [sortState, data]);

  if (sortedData.length === 0 && ui.search) {
    return (
      <div className="loader-no-item">
        <h3>No items match your search</h3>
      </div>
    );
  }

  if (!sortedData || !Array.isArray(sortedData) || loading) {
    return <Loader />;
  }

  const infoButtonStyle = {
    marginLeft: 8,
    width: 20,
    height: 20,
    borderRadius: "50%",
    border: "1px solid #cbd5e1",
    background: "#fff",
    color: "#0f172a",
    fontSize: 12,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    lineHeight: 1,
  };

  const sectionTitleWrapStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  };
  return (
    <>
      <Card top="true">
        <div className="payment-table-wrap">
          <Table>
            <Thead>
              <Tr className={`section-row ${ui.theme === "flame" ? "" : "dark-table-section"}`}>
                <Th colSpan="3" className="section-title section-title-pos">
                  <div style={sectionTitleWrapStyle}>
                    <div className="section-badge">POS</div>
                    <button
                      type="button"
                      style={infoButtonStyle}
                      onClick={() => setInfoModal("pos")}
                      title="POS info"
                    >
                      i
                    </button>
                  </div>
                </Th>

                <Th colSpan={accountingColsCount} className="section-title section-title-acc">
                  <div style={sectionTitleWrapStyle}>
                    <div className="section-badge">Accounting</div>
                    <button
                      type="button"
                      style={infoButtonStyle}
                      onClick={() => setInfoModal("accounting")}
                      title="Accounting info"
                    >
                      i
                    </button>
                  </div>
                </Th>
              </Tr>

              {scrollPos === true ? (
                <Tr className={`${ui.theme === "flame" ? "" : "dark-table"}`}>
                  <Sortable.Th
                    width="10%"
                    direction={sortValues[sortState[0]]}
                    onClick={() => changeSortState(0)}
                    className="th-pos"
                  >
                    Company
                  </Sortable.Th>

                  <Sortable.Th
                    width="15%"
                    direction={sortValues[sortState[1]]}
                    onClick={() => changeSortState(1)}
                    className="th-pos"
                  >
                    Payment Id
                  </Sortable.Th>

                  <Sortable.Th
                    width="20%"
                    direction={sortValues[sortState[2]]}
                    onClick={() => changeSortState(2)}
                    className="th-pos th-pos-last"
                  >
                    Payment Name
                  </Sortable.Th>

                  <Sortable.Th
                    width="20%"
                    direction={sortValues[sortState[3]]}
                    onClick={() => changeSortState(3)}
                    className="th-acc th-acc-first"
                  >
                    Account Id
                  </Sortable.Th>

                  <Sortable.Th
                    width="20%"
                    direction={sortValues[sortState[4]]}
                    onClick={() => changeSortState(4)}
                    className="th-acc"
                  >
                    Account Description
                  </Sortable.Th>

                  {!acombagoIntegration && (


                    <Sortable.Th
                    width="20%"
                    direction="unsorted"
                    onClick={() => {}}
                    className="th-acc"
                  >
                    Class/Dep/Loc <ClassDepLocInfo />
                  </Sortable.Th>


                  )}
                  <Sortable.Th
                    width="20%"
                    direction="unsorted"
                    onClick={() => {}}
                    className="th-acc"
                  >
                    {acombagoIntegration ? "Project" : "Customer"}
                  </Sortable.Th>

                  <Sortable.Th
                    width="20%"
                    direction={sortValues[sortState[5]]}
                    onClick={() => changeSortState(5)}
                    className="th-acc"
                  >
                    Over/Short Id <OverShortInfo />
                  </Sortable.Th>

                  <Sortable.Th
                    width="20%"
                    direction={sortValues[sortState[6]]}
                    onClick={() => changeSortState(6)}
                    className="th-acc"
                  >
                    Over/Short Description <OverShortInfo />
                  </Sortable.Th>

                  <Th width="10%" textAlign="right" className="th-acc th-plain th-action">
                    Action
                  </Th>
                </Tr>
              ) : (
                <Tr className={`${ui.theme === "flame" ? "" : "dark-table-fake"}`}>
                  <Th className="table-th-sticky th-pos" width="10%">
                    Company
                  </Th>

                  <Th className="table-th-sticky th-pos" width="15%">
                    Payment Id
                  </Th>

                  <Th className="table-th-sticky th-pos th-pos-last" width="20%">
                    Payment Name
                  </Th>

                  <Th className="table-th-sticky th-acc th-acc-first" width="20%">
                    Account Id
                  </Th>

                  <Th className="table-th-sticky th-acc" width="20%">
                    Account Description
                  </Th>

                  {!acombagoIntegration && (


                    <Th className="table-th-sticky th-acc th-plain" width="20%">
                    Class/Dep/Loc <ClassDepLocInfo />
                  </Th>


                  )}
                  <Th className="table-th-sticky th-acc th-plain" width="20%">
                    {acombagoIntegration ? "Project" : "Customer"}
                  </Th>

                  <Th className="table-th-sticky th-acc" width="20%">
                    Over/Short Id <OverShortInfo />
                  </Th>

                  <Th className="table-th-sticky th-acc" width="20%">
                    Over/Short Description <OverShortInfo />
                  </Th>

                  <Th
                    className="table-th-sticky th-acc th-plain th-action"
                    width="10%"
                    textAlign="right"
                  >
                    Action
                  </Th>
                </Tr>
              )}
            </Thead>

            <tbody className="t-body">
              {sortedData.map((item, index) => (
                <Row
                  key={item.paymenttype_id}
                  item={item}
                  accountsList={accountsList}
                  classList={classList}
                  d365DimensionsList={d365DimensionsList}
                  customersList={customersList}
                  isD365Integration={d365Integration || isD365Integration(item)}
                  hideClassDepartmentColumn={acombagoIntegration || isAcombaIntegration(item)}
                  rowIndex={index}
                />
              ))}
            </tbody>
          </Table>
        </div>
      </Card>

      {infoModal && (
        <div
          onClick={() => setInfoModal(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.45)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 640,
              background: "#fff",
              borderRadius: 16,
              boxShadow: "0 24px 80px rgba(0,0,0,0.25)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
                {infoModal === "pos" ? "POS Description" : "Accounting Description"}
              </div>

              <button
                type="button"
                onClick={() => setInfoModal(null)}
                aria-label="Close"
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: 20,
                  cursor: "pointer",
                  color: "#64748b",
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: 20, color: "#1e293b", lineHeight: 1.6, fontSize: 14 }}>
              {infoModal === "pos" ? (
                <div>
                  <div style={{ marginBottom: 10, fontWeight: 600 }}>Description:</div>
                  <div>
                    POS columns represent the original payment information coming from the POS
                    system, such as Company, Payment Id, and Payment Name.
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom: 10, fontWeight: 600 }}>Description:</div>
                  <div style={{ marginBottom: 14 }}>
                    If multiple payments share the same Account ID and Account Description, they
                    will be grouped and summed into a single line.
                  </div>

                  <div style={{ marginBottom: 8, fontWeight: 600 }}>Example:</div>

                  <div style={{ marginBottom: 4 }}>Amex -&gt; GL 1200 / Card</div>
                  <div style={{ marginBottom: 4 }}>Master -&gt; GL 1200 / Card</div>
                  <div style={{ marginBottom: 4 }}>Visa -&gt; GL 1200 / Card</div>

                </div>
              )}
            </div>

            <div
              style={{
                padding: "16px 20px",
                borderTop: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button size="small" fill="true" onClick={() => setInfoModal(null)}>
                &times;
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentTable;
